import React from 'react';
import { ShoppingBag, Eye, ArrowLeft, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_BASE_URL = 'https://rudrakshenterprises.liveindiatech.com';

// Helper function to fix local 127.0.0.1 and relative image URLs for live server
const getCorrectImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80';
    if (url.includes('127.0.0.1:8000') || url.includes('localhost:8000')) {
        return url.replace(/https?:\/\/(127\.0\.0\.1|localhost):8000/, 'https://rudrakshenterprises.liveindiatech.com');
    }
    if (url.startsWith('/')) {
        return `https://rudrakshenterprises.liveindiatech.com${url}`;
    }
    return url;
};

export default function CategoryPage({
    categoryId,
    cartCount,
    setIsCartOpen,
    setActiveCategory,
    setCurrentPage,
    addToCart,
    onViewDetails,
    setSearchQuery,
    searchQuery,
    pujaProducts = [],
    herbalProducts = [],
    jaivikProducts = [],
    ayurvedicProducts = [],
    organicProducts = [],
    adminProducts = []
}) {
    console.log("Active Category ID received in CategoryPage:", categoryId);

    // Categories data mapping with explicit arrays
    const allCategoriesData = {
        puja: {
            name: 'Puja Samagri Collection',
            description: 'Pure and traditional items for your daily rituals.',
            products: pujaProducts
        },
        herbal: {
            name: 'Herbal Products Range',
            description: 'Nourish your skin and hair naturally.',
            products: herbalProducts
        },
        jaivik: {
            name: 'Jaivik Products (Chemical-Free)',
            description: 'Directly harvested pure Jaivik food items.',
            products: jaivikProducts
        },
        ayurvedic: {
            name: 'Ayurvedic Products Formulations',
            description: 'Classical immunity boosters and wellness formulas.',
            products: ayurvedicProducts
        },
        organic: {
            name: '100% Organic Lifestyle Products',
            description: 'Wholesome daily essentials for active living.',
            products: organicProducts
        }
    };

    // Normalized category key
    const cleanCatId = categoryId ? categoryId.toString().trim().toLowerCase() : 'all';
    const currentCategory = allCategoriesData[cleanCatId];

    let rawProducts = [];
    if (cleanCatId !== 'all' && currentCategory) {
        rawProducts = currentCategory.products || [];
    } else {
        // Agar 'all' hai ya koi match nahi mila toh sabhi products dikhayein
        rawProducts = [
            ...(pujaProducts || []),
            ...(herbalProducts || []),
            ...(jaivikProducts || []),
            ...(ayurvedicProducts || []),
            ...(organicProducts || [])
        ];
    }

    // Remove duplicates safely using Map
    const uniqueRawProducts = Array.from(new Map(rawProducts.map(item => [item.id || item.name, item])).values());

    // Filter products if search query is present
    const displayedProducts = searchQuery && searchQuery.trim() !== ''
        ? uniqueRawProducts.filter(p =>
            (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        : uniqueRawProducts;

    return (
        <div className="min-h-screen flex flex-col bg-white">
            {/* Navbar */}
            <Navbar
                cartCount={cartCount}
                setIsCartOpen={setIsCartOpen}
                setActiveCategory={setActiveCategory}
                setCurrentPage={setCurrentPage}
                setSearchQuery={setSearchQuery}
                adminProducts={adminProducts}
                pujaProducts={pujaProducts}
                herbalProducts={herbalProducts}
                jaivikProducts={jaivikProducts}
                ayurvedicProducts={ayurvedicProducts}
                organicProducts={organicProducts}
                onViewDetails={onViewDetails}
            />

            <main className="flex-1 w-full px-4 sm:px-8 md:px-12 lg:px-20 py-12 bg-gray-50/50">

                {/* Back to Home Button */}
                <button
                    onClick={() => {
                        if (typeof setSearchQuery === 'function') setSearchQuery('');
                        if (typeof setActiveCategory === 'function') setActiveCategory('all');
                        if (typeof setCurrentPage === 'function') setCurrentPage('home');
                    }}
                    className="mb-8 flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-xs font-bold hover:bg-gray-100 transition shadow-sm cursor-pointer border-none"
                >
                    <ArrowLeft size={16} /> Back to Home
                </button>

                {/* Header Title */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 mb-10 text-center">
                    <div className="inline-flex items-center gap-1.5 text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">
                        <Sparkles size={14} /> {searchQuery ? `Search Results for "${searchQuery}"` : 'Category Details'}
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
                        {searchQuery ? `Found ${displayedProducts.length} Results` : (currentCategory ? currentCategory.name : 'All Products Catalog')}
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">
                        {searchQuery ? `Showing matching products for your search` : (currentCategory ? currentCategory.description : 'Browse through our complete collection of natural and pure wellness products.')}
                    </p>
                </div>

                {/* Products Grid */}
                {displayedProducts.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 shadow-sm max-w-xl mx-auto">
                        <p className="text-base font-bold text-gray-700">No products found in this category.</p>
                        <p className="text-xs text-gray-400 mt-1">Please check back later or explore other categories.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {displayedProducts.map((product, index) => (
                            <ProductCard key={product.id || index} product={product} addToCart={addToCart} onViewDetails={onViewDetails} />
                        ))}
                    </div>
                )}

            </main>

            {/* Footer */}
            <Footer
                setActiveCategory={setActiveCategory}
                setCurrentPage={setCurrentPage}
                adminProducts={adminProducts}
                pujaProducts={pujaProducts}
                herbalProducts={herbalProducts}
                jaivikProducts={jaivikProducts}
                ayurvedicProducts={ayurvedicProducts}
                organicProducts={organicProducts}
                onViewDetails={onViewDetails}
            />
        </div>
    );
}

// Individual Product Card Component
function ProductCard({ product, addToCart, onViewDetails }) {
    const primaryImage = getCorrectImageUrl(product.image);
    const galleryImages = product.images && product.images.length > 0
        ? product.images.map(img => getCorrectImageUrl(img.image || img))
        : [];

    return (
        <div className="bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col justify-between pt-16 pb-6 px-6 relative group mt-12">

            {/* Image Container */}
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-32 h-32 bg-white rounded-2xl border-2 border-orange-200 shadow-md overflow-hidden flex items-center justify-center p-1">
                <img
                    src={primaryImage}
                    onError={(e) => { e.target.onerror = null; e.target.src = product.fallbackImage || 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80'; }}
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition duration-500 rounded-xl"
                />
            </div>

            <span className="absolute top-3 right-3 z-10 bg-orange-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow">
                {product.discount || 'Off 10%'}
            </span>

            <div className="text-center mt-6 flex-1 flex flex-col justify-between bg-white">
                <div>
                    <h3 className="font-extrabold text-gray-900 text-sm sm:text-base line-clamp-1">{product.name}</h3>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{product.description}</p>

                    <div className="mt-4 flex items-center justify-center gap-3">
                        <span className="text-xs text-gray-400 line-through">₹{product.original_price || product.originalPrice}</span>
                        <span className="text-lg font-black text-orange-600">₹{product.price}</span>
                    </div>

                    {galleryImages.length > 0 && (
                        <div className="flex items-center justify-center gap-1.5 mt-3">
                            {galleryImages.map((imgUrl, imgIdx) => (
                                <div key={imgIdx} className="w-8 h-8 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 p-0.5">
                                    <img src={imgUrl} alt="extra" className="w-full h-full object-cover rounded" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-2 gap-2">
                    <button
                        onClick={() => onViewDetails && onViewDetails(product)}
                        className="bg-gray-800 hover:bg-gray-900 text-white py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 shadow-sm cursor-pointer border-none"
                    >
                        <Eye size={14} /> Details
                    </button>
                    <button
                        onClick={() => addToCart(product)}
                        className="bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 shadow-sm cursor-pointer border-none"
                    >
                        <ShoppingBag size={14} /> Add
                    </button>
                </div>
            </div>
        </div>
    );
}